import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import {GoForwardIcon, LoadingSpinner, PayPalIcon} from 'components';
import Toast from 'components/Basic/Toast';
import {selectUserProfile} from 'features/User';
import {
  BROWSER_ENUM,
  PaymentService,
  PayPalGetSellerOnboardingDetailsResponse,
  ProviderProfile,
  SliceStatus,
} from 'interfaces';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {BROWSER} from 'utils';

import {digitalPracticeActions} from '../digitalPracticeSlice';

import ProcessingFee from './ProcessingFee';

export const PaypalOnboardingLink: FC = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const [status, setStatus] = useState(SliceStatus.idle);
  const paypalWindow = useRef<Window | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const isSafariBrowser = BROWSER === BROWSER_ENUM.SAFARI;

  const user = useSelector(selectUserProfile) as ProviderProfile;

  const loadPayPalScript = useCallback(() => {
    const script = document.createElement('script');
    script.id = 'paypal-js';
    script.async = true;
    script.src =
      'https://www.paypal.com/webapps/merchantboarding/js/lib/lightbox/partner.js';

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const cleanup = loadPayPalScript();

  const useEffectCleanup = () => {
    if (paypalWindow.current && !paypalWindow.current.closed) {
      paypalWindow.current.close();
    }
    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);
    }
    cleanup();
  };

  const handlePayPalOnboardingDetails = (
    paypalOnboardingDetails: PayPalGetSellerOnboardingDetailsResponse,
  ) => {
    if (!paypalOnboardingDetails.paymentsReceivable) {
      Toast({
        type: 'warning',
        message: t('paypalAccountRestriction'),
        autoClose: false,
      });
    }
    if (!paypalOnboardingDetails.primaryEmailConfirmed) {
      Toast({
        type: 'warning',
        message: t('verifyPaypalEmail'),
        autoClose: false,
      });
    }
  };

  const checkUserPayPalMerchantId = () => {
    if (user?.paypalMerchantId) {
      dispatch(
        digitalPracticeActions.getPayPalSellerOnboardingDetails({
          callback: handlePayPalOnboardingDetails,
        }),
      );
    }
  };

  useEffect(() => {
    checkUserPayPalMerchantId();
    return useEffectCleanup;
  }, []);

  const handleGetPayPalOnboardingReferralLink = (referralLink: string) => {
    const intervalFunction = () => {
      if (paypalWindow.current?.closed) {
        paypalWindow.current = null;
        clearInterval(intervalIdRef.current!);
      }
    };

    if (referralLink) {
      setStatus(SliceStatus.resolved);
      const url = `${referralLink}`;
      if (isSafariBrowser) {
        window.location.href = url; // Open in current tab for Safari
      } else {
        paypalWindow.current = window.open(url, '_blank');
      }
      intervalIdRef.current = setInterval(intervalFunction, 1000);
    } else {
      setStatus(SliceStatus.rejected);
      console.error('Error: Empty referral link');
    }
  };

  const handleProviderOnboardingToPaypal = () => {
    if (
      user?.paypalMerchantId &&
      user?.paypal?.onboardingStatus === 'completed'
    ) {
      if (isSafariBrowser) {
        window.location.href = 'https://www.paypal.com/signin'; // Open in current tab for Safari
      } else {
        window.open(
          'https://www.paypal.com/signin',
          '_blank',
          'noopener,noreferrer',
        );
      }
    } else {
      setStatus(SliceStatus.pending);
      if (paypalWindow.current) {
        paypalWindow.current.focus();
        setStatus(SliceStatus.resolved);
        return;
      }

      dispatch(
        digitalPracticeActions.getPayPalOnboardingReferralLink({
          callback: handleGetPayPalOnboardingReferralLink,
        }),
      );
    }
  };

  const isPreparingPayPalSellerOnboardingLink = status === SliceStatus.pending;

  return (
    <div className="w-full">
      {isPreparingPayPalSellerOnboardingLink ? (
        <div className="w-full flex items-center justify-between border rounded-lg h-14 px-5">
          <p className="text-lg text-gray-700">{t('loading_please_wait')}</p>
          <LoadingSpinner height={20} width={20} type="Oval" />
        </div>
      ) : (
        <button
          className="w-full flex items-center justify-between text-center text-lg  border rounded-lg h-14 px-5 hover:bg-blue-600/10 hover:border-transparent group"
          onClick={handleProviderOnboardingToPaypal}
        >
          <div className="flex items-center space-x-3">
            <PayPalIcon classes="w-6 h-auto" />
            <p className="text-left">
              {user.paypalMerchantId
                ? t('providerProfileMenuItems.revenue.dp.goToPaypal')
                : t('providerProfileMenuItems.revenue.dp.linkToPaypal')}
            </p>
          </div>
          <GoForwardIcon
            fillColor="#315eff"
            strokeColor="text-blue-600"
            strokeWidth={0.3}
            classes="group-hover:animate-shake"
          />
        </button>
      )}
      {user.paypalMerchantId ? null : (
        <p className="p-1 text-xs text-left text-gray-600 italic">
          <span className="text-red-600">*</span>
          <Trans i18nKey="connectPaypal">
            Please ensure to connect your PayPal <b>Business</b> account, not an
            individual account, to enable seamless transactions.
          </Trans>
        </p>
      )}

      <ProcessingFee
        countryCode={user?.countryOfResidence?.code}
        paymentService={PaymentService.PAYPAL}
      />
    </div>
  );
};

export default PaypalOnboardingLink;
